import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Badge,
    Stack,
    Divider,
} from '@mui/material';
import { RestartAlt as RestartIcon, Lock as LockIcon } from '@mui/icons-material';
import { apiClient } from 'utils/ApiClient';

interface BotConfig {
    status: string;
    targetStatus?: string;
    proxmoxid: string;
    registeredBy: string;
    updatedAt: string;
}

interface CompanyBotConfigurations {
    freeWhatsAppBot?: BotConfig;
    emailBot?: BotConfig;
    whatsAppBusinessBot?: BotConfig;
    telegramBot?: BotConfig;
}

const STANDARD_BOT_TYPE = 'freeWhatsAppBot' as const;

export const CompanyBotManager: React.FC<{ companyId: string }> = ({ companyId }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [botConfigurations, setBotConfigurations] = useState<CompanyBotConfigurations>({});
    const [defaultBotManager, setDefaultBotManager] = useState<string>('');
    const [rebootingBots, setRebootingBots] = useState<Set<string>>(new Set());

    const showActionsColumn = defaultBotManager !== 'ClientManaged';

    useEffect(() => {
        if (companyId) {
            fetchBotConfigurations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const fetchBotConfigurations = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiClient.get(`/company/${companyId}`);
            const companyData = (data as any).data || data;
            setDefaultBotManager(companyData?.configurations?.defaultBotManager || '');
            if (companyData?.configurations?.bot) {
                setBotConfigurations(companyData.configurations.bot);
            } else {
                setBotConfigurations({});
            }
        } catch (err) {
            setError('Failed to fetch bot configurations');
        } finally {
            setLoading(false);
        }
    };

    const handleRebootBot = async (botType: string) => {
        try {
            setRebootingBots((prev) => new Set(prev).add(botType));
            setError(null);
            const response = await apiClient.patch(`/iu/botmanager/company/${companyId}/updateconfigurations`, {
                botType,
                targetStatus: 'restart',
            });
            if ((response as any)?.data) {
                await fetchBotConfigurations();
            }
        } catch (err) {
            setError('Failed to reboot Standard BOT');
        } finally {
            setRebootingBots((prev) => {
                const newSet = new Set(prev);
                newSet.delete(botType);
                return newSet;
            });
        }
    };

    const getStatusChip = (status: string) => {
        const normalizedStatus = (status || '').trim().toLowerCase();
        const isOnline = normalizedStatus === 'online';
        const displayStatus = normalizedStatus.toUpperCase();
        return (
            <Chip
                label={displayStatus}
                color={isOnline ? 'success' : 'error'}
                size="small"
                variant="filled"
            />
        );
    };

    const isBotInProgress = (botConfig: BotConfig) => {
        return botConfig?.status && botConfig?.targetStatus && botConfig.status !== botConfig.targetStatus;
    };

    const getInProgressBadge = () => {
        return (
            <Chip
                label="In progress"
                size="small"
                sx={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    fontWeight: 'bold',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.7, transform: 'scale(1.05)' },
                        '100%': { opacity: 1, transform: 'scale(1)' },
                    },
                    '& .MuiChip-label': { fontSize: '0.75rem' },
                }}
            />
        );
    };

    const formatTimestamp = (timestamp: string) => {
        if (!timestamp) return '-';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch {
            return timestamp;
        }
    };

    const premiumBadgeSx = {
        '& .MuiBadge-badge': {
            backgroundColor: '#FFD700',
            color: '#8B4513',
            minWidth: 20,
            height: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
    };

    const PremiumChip = () => (
        <Badge badgeContent={<LockIcon sx={{ fontSize: 12, color: '#8B4513' }} />} sx={premiumBadgeSx}>
            <Chip
                label="Premium"
                size="small"
                sx={{
                    backgroundColor: '#FFD700',
                    color: '#8B4513',
                    fontWeight: 'bold',
                    '& .MuiChip-label': { fontSize: '0.75rem' },
                }}
            />
        </Badge>
    );

    if (loading) {
        return (
            <Box width="100%" display="flex" justifyContent="center" alignItems="center" minHeight="320px">
                <CircularProgress />
            </Box>
        );
    }

    const botConfig = botConfigurations[STANDARD_BOT_TYPE];
    const isPremium = !botConfig;

    return (
        <Box width="100%">
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {Object.keys(botConfigurations).length > 0 && (
                <Card sx={{ borderRadius: '12px', boxShadow: 3, mt: 2 }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                            BOT Status
                        </Typography>

                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box
                                sx={{
                                    px: { xs: 2, sm: 2.5 },
                                    py: 2,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        Standard BOT
                                    </Typography>
                                    {isPremium && <LockIcon sx={{ fontSize: 18, color: '#FFD700' }} />}
                                </Box>
                                <Box sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
                                    {isPremium ? (
                                        <PremiumChip />
                                    ) : botConfig?.status ? (
                                        getStatusChip(botConfig.status)
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            —
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Divider />

                            <Stack spacing={2} sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Location
                                    </Typography>
                                    {isPremium ? (
                                        <PremiumChip />
                                    ) : (
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {botConfig?.proxmoxid || '—'}
                                        </Typography>
                                    )}
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Registered By
                                    </Typography>
                                    {isPremium ? (
                                        <PremiumChip />
                                    ) : (
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {botConfig?.registeredBy || '—'}
                                        </Typography>
                                    )}
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Last Updated
                                    </Typography>
                                    {isPremium ? (
                                        <PremiumChip />
                                    ) : (
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {formatTimestamp(botConfig?.updatedAt || '')}
                                        </Typography>
                                    )}
                                </Box>

                                {showActionsColumn && (
                                    <Box pt={0.5}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Actions
                                        </Typography>
                                        {isPremium ? (
                                            <PremiumChip />
                                        ) : isBotInProgress(botConfig!) ? (
                                            getInProgressBadge()
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                startIcon={<RestartIcon />}
                                                onClick={() => handleRebootBot(STANDARD_BOT_TYPE)}
                                                disabled={rebootingBots.has(STANDARD_BOT_TYPE)}
                                                size="small"
                                                fullWidth
                                                sx={{ maxWidth: { sm: 200 } }}
                                            >
                                                {rebootingBots.has(STANDARD_BOT_TYPE) ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    'Reset'
                                                )}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Stack>
                        </Card>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};
